/**
 * 알림 채널과 발송 로그.
 *
 * 저장소는 **보내지 않는다** — 채널 설정을 보관하고 결과를 기록할 뿐이고,
 * 실제 HTTP 는 `src/modules/notify/adapters.ts` 가 한다. 그래야 테스트가
 * 네트워크 없이 돌고, 실패한 발송도 남는다.
 */

import { OrgScopedRow } from "../common/types";

export const NOTIFY_CHANNEL_TYPES = ["dooray", "slack"] as const;
export type NotifyChannelType = (typeof NOTIFY_CHANNEL_TYPES)[number];

export const NOTIFY_CHANNEL_LABEL: Record<NotifyChannelType, string> = {
  dooray: "두레이",
  slack: "슬랙",
};

/** 보낼 만한 사건. 화면·웹훅이 이 코드로만 부른다. */
export const NOTIFY_EVENTS = [
  "intake.created",
  "quote.sent",
  "quote.viewed",
  "quote.decided",
  "contract.viewed",
  "contract.signed",
  "project.log_added",
  "change_order.decided",
  "project.completed",
  "project.start_day",
  "project.log_reminder",
  "project.customer_message",
  "intake.unanswered",
] as const;
export type NotifyEvent = (typeof NOTIFY_EVENTS)[number];

export const NOTIFY_EVENT_LABEL: Record<NotifyEvent, string> = {
  "intake.created": "접수 등록",
  "quote.sent": "견적 발송",
  "quote.viewed": "고객 열람",
  "quote.decided": "수주·이탈 확정",
  "contract.viewed": "계약서 고객 열람",
  "contract.signed": "계약 체결",
  "project.log_added": "현장일지 작성",
  "change_order.decided": "변경공사 고객 결정",
  "project.completed": "현장 준공",
  "project.start_day": "착공일",
  "project.log_reminder": "특이사항 독촉",
  "project.customer_message": "고객 대화",
  "intake.unanswered": "미응답 경고",
};

/** 조직 설정 `notify` 한 칸에 JSON 배열로 굳혀 둔다. */
export interface NotifyChannel {
  type: NotifyChannelType;
  webhook_url: string;
  events: NotifyEvent[];
}

export const NOTIFY_STATUSES = ["sent", "failed", "skipped"] as const;
export type NotifyStatus = (typeof NOTIFY_STATUSES)[number];

/** 웹훅 본문을 만들 재료. 어댑터가 채널 모양으로 바꾼다. */
export type NotifyPayload = Record<string, unknown>;

export interface NotifyLog extends OrgScopedRow {
  channel_type: NotifyChannelType;
  event: NotifyEvent;
  payload: NotifyPayload;
  status: NotifyStatus;
  /**
   * 왜 그렇게 됐는지 한 줄. 실패 사유이거나, `skipped` 일 때는 건너뛴 이유
   * (dry-run · 구독하지 않는 이벤트) 다 — 「왜 알림이 안 왔지」 의 답이 여기 있다.
   */
  error: string | null;
}

export const isNotifyChannelType = (v: unknown): v is NotifyChannelType =>
  typeof v === "string" &&
  (NOTIFY_CHANNEL_TYPES as readonly string[]).includes(v);

export const isNotifyEvent = (v: unknown): v is NotifyEvent =>
  typeof v === "string" && (NOTIFY_EVENTS as readonly string[]).includes(v);

/**
 * 설정 문자열을 채널 목록으로. 깨진 값·모르는 코드는 조용히 버린다 —
 * 알림 설정 한 줄이 깨졌다고 설정 화면 전체가 죽으면 안 된다.
 */
export const parseNotifyChannels = (value: string | null): NotifyChannel[] => {
  if (!value) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.flatMap((row): NotifyChannel[] => {
    if (typeof row !== "object" || row === null) return [];
    const rec = row as Record<string, unknown>;
    if (!isNotifyChannelType(rec.type)) return [];
    if (typeof rec.webhook_url !== "string" || rec.webhook_url === "") {
      return [];
    }
    const events = Array.isArray(rec.events)
      ? rec.events.filter(isNotifyEvent)
      : [];
    return [{ type: rec.type, webhook_url: rec.webhook_url, events }];
  });
};

export const stringifyNotifyChannels = (channels: NotifyChannel[]): string =>
  JSON.stringify(channels);
